import { useId } from 'react';
import type { SubjectStatus } from '@ni/shared';
import { statusTone } from '@ni/shared';

/**
 * **One segment of an analysis chart**: what it counts, and how it is colored.
 * A status split colors by the status's tone (`tone`); any other chart by its
 * palette class (`swatch`, analysis.css), and an absence is `hatch`.
 */
export interface ChartSegment {
  key: string;
  label: string;
  count: number;
  /** A status tone name, for a chart that splits people by status. */
  tone?: string;
  /** A chart palette class (`c-raised`, `c-check`, …) or `hatch` for an absence. */
  swatch?: string;
  /** A hover hint for a label whose name alone could be misread. */
  hint?: string;
}

/** A status's segment, colored by its tone. */
export function statusSegment(
  vertical: string,
  status: SubjectStatus,
  label: string,
  count: number,
): ChartSegment {
  return { key: status, label, count, tone: statusTone(vertical, status) };
}

const C = 100;
const R = 76;
/** Degrees left between neighbouring segments. */
const GAP = 2.4;

function point(degrees: number): string {
  const rad = ((degrees - 90) * Math.PI) / 180;
  return `${(C + R * Math.cos(rad)).toFixed(2)} ${(C + R * Math.sin(rad)).toFixed(2)}`;
}

/**
 * **A ring around its total**, drawn to scale: segments start at twelve
 * o'clock and run clockwise in legend order. Every segment has its number in
 * the legend beside it, so the ring itself is a picture, not the record.
 */
export function Ring({
  segments,
  totalLabel,
}: {
  segments: readonly ChartSegment[];
  totalLabel: string;
}) {
  const hatchId = useId().replace(/:/g, '');
  const total = segments.reduce((sum, one) => sum + one.count, 0);
  const shown = segments.filter((one) => one.count > 0);
  let at = 0;
  return (
    <div className="an-ring">
      <svg viewBox="0 0 200 200" aria-hidden="true">
        <defs>
          <pattern
            id={hatchId}
            width="5"
            height="5"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="5" height="5" className="hatch-ground" />
            <line x1="0" y1="0" x2="0" y2="5" className="hatch-line" />
          </pattern>
        </defs>
        {total === 0 ? <circle className="an-seg empty" cx={C} cy={C} r={R} /> : null}
        {shown.map((one) => {
          const sweep = (one.count / total) * 360;
          const gap = shown.length > 1 ? GAP : 0;
          const from = at + gap / 2;
          const to = at + sweep - gap / 2;
          at += sweep;
          const props = {
            className: `an-seg ${one.tone ? 'tone' : (one.swatch ?? '')}`,
            'data-tone': one.tone,
            stroke: one.swatch === 'hatch' ? `url(#${hatchId})` : undefined,
          };
          const title = <title>{`${one.label}: ${one.count} of ${total}`}</title>;
          return shown.length === 1 ? (
            <circle key={one.key} {...props} cx={C} cy={C} r={R}>
              {title}
            </circle>
          ) : (
            <path
              key={one.key}
              {...props}
              d={`M${point(from)} A${R} ${R} 0 ${to - from > 180 ? 1 : 0} 1 ${point(to)}`}
            >
              {title}
            </path>
          );
        })}
      </svg>
      <div className="an-ring-total">
        <span className="n num">{total}</span>
        <span className="l">{totalLabel}</span>
      </div>
    </div>
  );
}

/**
 * **The chart's legend**, every segment with its count, zero included. With
 * `onPick` each row is a button that picks its group; pressed again, it lets go.
 */
export function Legend({
  segments,
  picked = null,
  onPick,
}: {
  segments: readonly ChartSegment[];
  picked?: string | null;
  onPick?: (key: string | null) => void;
}) {
  return (
    <ul className={onPick ? 'an-key picks' : 'an-key'}>
      {segments.map((one) => {
        const inner = (
          <>
            <span
              className={`an-sw ${one.tone ? 'tone' : (one.swatch ?? '')}`}
              data-tone={one.tone}
            />
            <span className="lab">{one.label}</span>
            <span className="n num">{one.count}</span>
          </>
        );
        return (
          <li key={one.key} className={one.count === 0 ? 'zero' : undefined}>
            {onPick ? (
              <button
                type="button"
                title={one.hint}
                aria-pressed={picked === one.key}
                onClick={() => onPick(picked === one.key ? null : one.key)}
              >
                {inner}
              </button>
            ) : (
              inner
            )}
          </li>
        );
      })}
    </ul>
  );
}
